import _ from 'underscore';
import Promise from 'bluebird';
import {
  ImageExperienceImage,
  constants,
} from '@bingads-webui-campaign-react/image-experience-model';
import {
  getAutoCroppedImages,
  ImageRepository,
} from '@bingads-webui-campaign-react/image-experience';
import { DependencyResolver } from '@bingads-webui-campaign/dependency-resolver';
import { queryify } from '@bingads-webui/url-util';
import { getCenterCroppedSettings } from '@bingads-webui-campaign/image-experience-utils';
import { getMultiImages, getLogoExperience, getPortraitExperience } from './data-process';
import { processImagePlacementsMetadata } from './image-metadata-util';

const {
  subTypeToType,
  getImageTypesWithOrder,
  logoSubtypes,
  portraitSubtypes,
} = constants;

const autoFillMissingImagesAsync = (imageObjArr) => {
  const autoCroppedImages = getAutoCroppedImages({
    originalImageToCrop: imageObjArr[0],
    isMultimediaAd: false,
  });
  const imagesToReturn = [];

  _.each(autoCroppedImages, (croppedImage) => {
    const savedImage = _.find(imageObjArr, imageObj => imageObj.Type === croppedImage.Type);
    if (savedImage) {
      imagesToReturn.push(savedImage);
    } else {
      imagesToReturn.push(croppedImage);
    }
  });

  return imagesToReturn;
};

const imageAssetCenterCrop = (asset) => {
  const {
    SourceHeight,
    SourceWidth,
    SourceX,
    SourceY,
  } = getCenterCroppedSettings({
    SourceHeight: asset.CropHeight,
    SourceWidth: asset.CropWidth,
    SourceX: 0,
    SourceY: 0,
  }, 1.91);

  return {
    ...asset,
    CropX: SourceX,
    CropY: SourceY,
    CropWidth: SourceWidth,
    CropHeight: SourceHeight,
  };
};

export async function uploadRecommendImage(images, props, recommendAssetIdImageUrlMap) {
  const config = DependencyResolver.get('component/config/index');

  const odataToken = config.get('odata.token');
  const odataPath = config.get('odata.url') + config.get('odata.path');
  const odataRootUrl = config.get('CampaignAzureDomain');

  const { cid, aid } = props;
  const oDataConfig = {
    odataRootUrl,
    odataPath,
    odataToken,
  };

  const imagesWithoutId = images.filter(image => !image.Asset.Id);
  const urls = imagesWithoutId
    .map(image => ({ url: image.Asset.ImageUrl }));
  const imageRepository = new ImageRepository(cid, aid, oDataConfig);

  const ids = await imageRepository.bulkSaveImagesByUrl(urls);

  // save the mapping for log
  _.each(_.filter(images, i => !!i.Asset.Id), (image) => {
    recommendAssetIdImageUrlMap[image.Asset.Id] = image.Asset.ImageUrl; // eslint-disable-line no-param-reassign
  });
  _.each(ids, (id, index) => {
    recommendAssetIdImageUrlMap[id] = _.get(urls, [index, 'url']); // eslint-disable-line no-param-reassign
  });

  // eslint-disable-next-line
  const promises = imagesWithoutId.map((image, index) => {
    return imageRepository.getImageByIdAsync(ids[index]).then((imageInfo) => {
      image.Asset = { // eslint-disable-line no-param-reassign
        ...image.Asset,
        Id: ids[index],
        IdString: ids[index],
        SubType: 'LandscapeImageMedia',
        CropHeight: imageInfo.Height || 800,
        CropWidth: imageInfo.Width || 400,
      };
    }).catch(() => {
      image.Asset = { // eslint-disable-line no-param-reassign
        ...image.Asset,
        Id: ids[index],
        IdString: ids[index],
        SubType: 'LandscapeImageMedia',
        CropHeight: 800,
        CropWidth: 400,
      };
    }).finally(() => {
      image.Asset = imageAssetCenterCrop(image.Asset); // eslint-disable-line no-param-reassign
    });
  });

  await Promise.all(promises);
  return ids;
}

const getImageProps = (asset, imageToUrlMap, isIncluded, isMultimediaAd) => {
  const idString = asset.IdString || String(asset.Id);
  const assetExtraInfo = imageToUrlMap[idString];
  const isLogo = asset.SubType in constants.logoSubtypes;
  return {
    id: isMultimediaAd && isLogo ? `${idString}-${asset.SubType}` : idString,
    baseId: assetExtraInfo.baseId,
    metadata: assetExtraInfo.metadata,
    src: assetExtraInfo.url,
    type: subTypeToType[asset.SubType],
    associationType: asset.SubType,
    isLogo,
    width: assetExtraInfo.width,
    height: assetExtraInfo.height,
    cropSettings: {
      SourceX: asset.CropX ? asset.CropX : 0,
      SourceY: asset.CropY ? asset.CropY : 0,
      SourceWidth: asset.CropWidth ?
        asset.CropWidth : assetExtraInfo.width,
      SourceHeight: asset.CropHeight ?
        asset.CropHeight : assetExtraInfo.height,
    },
    isIncluded,
    isRecommendedImage: asset.IsRecommendedImage,
    disableEdit: asset.DisableEdit,
    originalUrl: assetExtraInfo.originalUrl,
    cropSettingOnOriginal: assetExtraInfo.cropSettingOnOriginal,
    placementSettings: assetExtraInfo.placementSettings,
    originalWidth: assetExtraInfo.originalWidth,
    originalHeight: assetExtraInfo.originalHeight,
  };
};

const getImageTasks = (mTAssetArrays, baseURL, odata, isImageFittingEnabled) => {
  let imageIdsToFetch = _.map(mTAssetArrays, mtAsset => mtAsset.Asset.IdString || mtAsset.Asset.Id);

  imageIdsToFetch = _.compact(_.uniq(imageIdsToFetch));

  return _.map(imageIdsToFetch, (imageId) => {
    let url = `${baseURL}Images(${imageId})`;
    if (isImageFittingEnabled) {
      url = `${url}?AdType=Audience`;
    }

    return odata.get(url)
      .then(response => ({
        ...response,

        // &c=0 means no crop. It wil help convert tiff to jpg so that img tag can be displayed.
        // This is to mitigate unexpected MMA demand associated with tiff images caused by a MT bug
        // Attention: this is not an official support for tiff format.
        // If we want to officially support a new image type, need more changes/tests.
        Url: queryify(response.Url, { c: 0 }),
      }));
  });
};

// fetch image info as well as base image info if any
const getAllImagesInfoFromMTAssets = ({
  mTAssetArrays,
  baseURL,
  odata,
  isAdCreatorEnabled,
  isImageFittingEnabled,
}) => {
  const imageInfoArr = [];

  return Promise.all(getImageTasks(mTAssetArrays, baseURL, odata, isImageFittingEnabled))
    .then((values) => {
      imageInfoArr.push(...values);

      if (isAdCreatorEnabled) {
        const existingIds = _.pluck(imageInfoArr, 'Id');
        const baseAssetArrays = _.chain(values)
          .pluck('BaseIdString')
          .compact()
          .difference(existingIds)
          .map(Id => ({ Asset: { Id } }))
          .value();

        if (!_.isEmpty(baseAssetArrays)) {
          return Promise.all(getImageTasks(baseAssetArrays, baseURL, odata, isImageFittingEnabled));
        }
      }

      return [];
    })
    .then((baseImageInfos) => {
      imageInfoArr.push(...baseImageInfos);

      const ret = _.reduce(imageInfoArr, (memo, value) => _.extend(memo, {
        [value.IdString]: _.extend({
          url: value.Url,
          height: value.Height,
          sourceType: value.SourceType,
          width: value.Width,
        }, isAdCreatorEnabled && {
          baseId: value.BaseIdString,
          metadata: value.Metadata,
        }, {
          originalUrl: value.OriginalUrl,
          cropSettingOnOriginal: value.CropSettingOnOriginal,
          placementSettings: value.PlacementSettings,
          originalHeight: value.OriginalHeight,
          originalWidth: value.OriginalWidth,
          roiInfo: isImageFittingEnabled ? value.ROIInfo : undefined,
        }),
      }), {});

      return ret;
    });
};

const buildAssetModels = ({
  mTAssetArrays,
  imageToUrlMap,
  imageTypesWithOrder,
  isMultimediaAd,
}) => {
  // build ImageExperienceImage for all existing placements
  let imageObjArr = _.map(
    mTAssetArrays,
    mtAsset => new ImageExperienceImage(getImageProps(mtAsset.Asset, imageToUrlMap, true, isMultimediaAd))
  );

  imageObjArr = imageObjArr.sort((imageObj1, imageObj2) => {
    const obj1Inx = imageTypesWithOrder.indexOf(imageObj1.Type);
    const obj2Inx = imageTypesWithOrder.indexOf(imageObj2.Type);

    if (obj1Inx < obj2Inx) {
      return -1;
    }
    return 1;
  });

  return imageObjArr;
};

const autoFillMissingImageIdAndSizeFromRecommendation = (mTAssetArrays, imageToUrlMap) => {
  _.each(mTAssetArrays, (mTAsset) => {
    if (_.isNull(mTAsset.Asset.Id)) {
      // Use image url as id because id is necessary field in ad preview components.
      _.extend(mTAsset.Asset, {
        Id: mTAsset.Asset.ImageUrl,
      });

      // Fill image url, height and width from recommendation.
      _.extend(imageToUrlMap, {
        [mTAsset.Asset.Id]: {
          url: mTAsset.Asset.ImageUrl,
          height: mTAsset.Asset.ImageHeight,
          width: mTAsset.Asset.ImageWidth,
          isRecommendedImage: mTAsset.Asset.IsRecommendedImage,
        },
      });
    }
  });
};

const getMultiImagesFromMTAssetsAsync = (
  odata,
  baseURL,
  mTAssetArrays,
  isAdCreatorEnabled,
  isImproveMMA,
  isMMAV2Enabled,
  isBoostPortraitImageEnabled,
  isBoost200x100ImageEnabled,
  isBoostNewAspectRatioSupportEnabled,
  isImageFittingEnabled,
  isInlineAssetEditorRefreshEnabled = false
) => {
  const options = {
    mTAssetArrays,
    baseURL,
    odata,
    isAdCreatorEnabled,
    isImageFittingEnabled,
  };

  return getAllImagesInfoFromMTAssets(options)
    .then((imageToUrlMap) => {
      // When image asset id is null, auto fill image url and size from recommendation.
      if (isImproveMMA) {
        autoFillMissingImageIdAndSizeFromRecommendation(mTAssetArrays, imageToUrlMap);
      }

      const logoAssets = mTAssetArrays
        .map(mtAsset => mtAsset.Asset).filter(asset => logoSubtypes[asset.SubType]);
      const logoExperiences = getLogoExperience(logoAssets, imageToUrlMap);

      const imageAsset = mTAssetArrays
        .map(mtAsset => mtAsset.Asset).filter((asset) => {
          if (isBoostPortraitImageEnabled) {
            return !logoSubtypes[asset.SubType] && !portraitSubtypes[asset.SubType];
          }
          return !logoSubtypes[asset.SubType];
        });

      const portraitAssets = mTAssetArrays
        .map(mtAsset => mtAsset.Asset).filter(asset => portraitSubtypes[asset.SubType]);
      const portraitExperiences = getPortraitExperience(portraitAssets, imageToUrlMap);

      const imageExperiences = getMultiImages(
        imageAsset,
        imageToUrlMap,
        {
          isAdCreatorEnabled,
          isMMAV2Enabled,
          isBoost200x100ImageEnabled,
          isBoostNewAspectRatioSupportEnabled,
          isImageFittingEnabled,
          isInlineAssetEditorRefreshEnabled,
        }
      );

      if (isImageFittingEnabled) {
        _.each(imageExperiences, (image) => {
          _.each(image, (placement) => {
            const { roiInfo } = imageToUrlMap[placement.IdString] || {};
            placement.setROIInfo(roiInfo, isMMAV2Enabled, true);
          });
        });
      }

      return [...logoExperiences, ...imageExperiences, ...portraitExperiences];
    });
};

const groupImageObjectsByBaseImage = (imageObjArr, isAdCreatorEnabled) => {
  const initialObj = {
    image: {},
    logo: {},
  };

  return imageObjArr.reduce((memo, imageObj) => {
    const id = isAdCreatorEnabled ? imageObj.BaseId : imageObj.Id;
    const category = imageObj.isLogo ? 'logo' : 'image';

    const previousPlacements = memo[category][id] ? memo[category][id].placements : [];

    return {
      image: {
        ...memo.image,
      },
      logo: {
        ...memo.logo,
      },
      [category]: {
        ...memo[category],
        [id]: { baseId: id, placements: [...previousPlacements, imageObj] },
      },
    };
  }, initialObj);
};

const getImagesFromMTAssetsAsync = (
  odata,
  baseURL,
  mTAssetArrays,
  isMultimediaAd = false,
  isAdCreatorEnabled = false,
  isCombinedMsanAndMmaAspectRatios = false,
  isStoreAppProng = false,
  isStoreAppSpotLight = false,
  isStoreAppTransparentLogo = false,
  isLinkedInAd = false,
  isImageFittingEnabled = false,
  isPMaxAspectRatiosV2Enabled = false,
  isInlineAssetEditorRefreshEnabled = false
) => {
  const options = {
    mTAssetArrays,
    baseURL,
    odata,
    isAdCreatorEnabled,
    isImageFittingEnabled,
  };

  return getAllImagesInfoFromMTAssets(options).then((imageToUrlMap) => {
    const imageTypesWithOrder =
      getImageTypesWithOrder(isMultimediaAd, isCombinedMsanAndMmaAspectRatios, isStoreAppProng, isStoreAppSpotLight, isStoreAppTransparentLogo, isLinkedInAd, isPMaxAspectRatiosV2Enabled);

    // When image asset id is null, auto fill image url and size from recommendation.
    autoFillMissingImageIdAndSizeFromRecommendation(mTAssetArrays, imageToUrlMap);

    // build ImageExperienceImage for all existing placements
    let imageObjArr = buildAssetModels({
      mTAssetArrays,
      imageToUrlMap,
      imageTypesWithOrder,
      isMultimediaAd,
    });

    if (isMultimediaAd || isStoreAppProng || isStoreAppSpotLight || isStoreAppTransparentLogo || isLinkedInAd) {
      // for MMA, UI state imageList is 2-dimensional array.
      // Each image element consists of multiple placements state.
      const mmaAspectRatios = constants.getAspectRatios({
        isMultimediaAd,
        isMultimediaAdinMSANEnabled: false,
        isCombinedMsanAndMmaAspectRatios,
        isStoreAppProng,
        isStoreAppSpotLight,
        isStoreAppTransparentLogo,
        isLinkedInAd,
        isPMaxAspectRatiosV2Enabled,
      });

      imageObjArr = _.filter(imageObjArr, image => image.type in mmaAspectRatios);

      // group by base image
      let imageObjArrById = groupImageObjectsByBaseImage(imageObjArr, isAdCreatorEnabled);

      // process placements metadata and fill in missing placements for each base image
      imageObjArrById = _.map([
        ..._.values(imageObjArrById.image),
        ..._.values(imageObjArrById.logo),
      ], ({ baseId, placements: imageArr }) => {
        const baseImageId = baseId.toString().indexOf('-') > -1 ? baseId.match(/(\S*)-/)[1] : baseId;

        // preprocess metadata in existing placement
        if (isAdCreatorEnabled) {
          processImagePlacementsMetadata(imageArr, baseImageId, imageToUrlMap[baseImageId], isImageFittingEnabled, isCombinedMsanAndMmaAspectRatios, isInlineAssetEditorRefreshEnabled);
        }

        // fill in missing placement and generate full list of placements
        const missingTypes = _.difference(imageTypesWithOrder, _.pluck(imageArr, 'type'));
        const autoCroppedImages =
          getAutoCroppedImages({
            originalImageToCrop: isAdCreatorEnabled
              ? _.first(imageArr).BaseImage : _.first(imageArr),
            isMultimediaAd: true,
            imageOption: _.first(imageArr).isLogo ?
              constants.imageOptionKeys.Logo : constants.imageOptionKeys.Image,
            isRecommendedImage: _.first(imageArr).isRecommendedImage,
            isCombinedMsanAndMmaAspectRatios,
            isLinkedInAd,
            disableEdit: _.first(imageArr).disableEdit,
            isPMaxAspectRatiosV2Enabled,
          });

        return _.compact(_.map(imageTypesWithOrder, (type) => {
          const isMissingType = missingTypes.indexOf(type) >= 0;
          let placement;

          if (isMissingType) {
            placement = _.find(autoCroppedImages, image => image.type === type);
            if (placement) {
              placement.setIncluded(false);

              if (isAdCreatorEnabled) {
                placement.setBaseImage(imageArr[0].BaseImage.clone());
                placement.setEffectSettings(placement.BaseImage.EffectSettings);
              }
            }
          } else {
            placement = _.find(imageArr, image => image.type === type);
          }

          return placement;
        }));
      });

      return _.values(imageObjArrById);
    }

    if (imageObjArr.length === imageTypesWithOrder.length) {
      return imageObjArr;
    }

    return autoFillMissingImagesAsync(imageObjArr);
  });
};

export {
  getImagesFromMTAssetsAsync,
  getMultiImagesFromMTAssetsAsync,
  getImageProps,
  getAllImagesInfoFromMTAssets,
};
